import React, { useEffect, useState } from "react";
import { LifeBuoy, Lock, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

const Login = ({ onLogin }) => {
  const [images, setImages] = useState([]);
  const [active, setActive] = useState(0);
  const [rotationMs, setRotationMs] = useState(5000);
  const [loadingImages, setLoadingImages] = useState(true);
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [registrationEnabled, setRegistrationEnabled] = useState(false);

  useEffect(() => {
    let alive = true;
    fetch("/api/onboarding.php", { credentials: "include", cache: "no-store" })
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (alive) setRegistrationEnabled(data?.enabled === true); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const fetchImages = async () => {
      try {
        const res = await fetch("/api/login-slider.php");
        if (!res.ok) throw new Error("HTTP " + res.status);
        const data = await res.json();
        setImages(Array.isArray(data.images) ? data.images : []);
        if (Number(data.rotation_ms) >= 1000) {
          setRotationMs(Number(data.rotation_ms));
        }
      } catch (err) {
        console.error("Errore caricamento slider:", err);
      } finally {
        setLoadingImages(false);
      }
    };
    fetchImages();
  }, []);

  useEffect(() => {
    if (active >= images.length) {
      setActive(0);
    }
  }, [images.length, active]);

  useEffect(() => {
    if (images.length <= 1) return;
    const t = setInterval(() => {
      setActive((prev) => (prev + 1) % images.length);
    }, rotationMs);
    return () => clearInterval(t);
  }, [images, rotationMs]);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const fd = new FormData();
    fd.append("email", form.email);
    fd.append("password", form.password);

    try {
      const res = await fetch("/api/login.php", {
        method: "POST",
        body: fd,
        credentials: "include",
      });

      const ct = res.headers.get("content-type") || "";
      if (ct.includes("application/json")) {
        const data = await res.json();
        if (data.success) {
          if (onLogin) onLogin(data);
          else window.location.href = data.redirect_to || (data.inspector_only ? "/ispettore" : "/");
        } else {
          setError(data.message || "Credenziali non valide");
        }
      } else if (res.ok) {
        window.location.href = "/";
      } else {
        setError("Errore di login (" + res.status + ")");
      }
    } catch (err) {
      console.error(err);
      setError("Errore di rete");
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-100">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-slate-900">
        {loadingImages ? (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-white/70">
            Caricamento immagini...
          </div>
        ) : images.length ? (
          images.map((img, idx) => (
            <img
              key={`${img}-${idx}`}
              src={img}
              alt={`slide-${idx + 1}`}
              className={`absolute inset-0 h-full w-full object-contain object-center transition-opacity duration-1000 ${
                idx === active ? "opacity-100" : "opacity-0"
              }`}
            />
          ))
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-white/70">
            Nessuna immagine configurata.
          </div>
        )}

        <div className="absolute inset-0 bg-gradient-to-r from-black/10 via-transparent to-black/20" />

        {images.length > 1 && (
          <div className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 rounded-full bg-black/30 px-3 py-2 backdrop-blur-sm">
            {images.map((_, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setActive(idx)}
                aria-label={`Vai alla slide ${idx + 1}`}
                className={`h-2 rounded-full transition-all ${
                  idx === active ? "w-8 bg-white" : "w-2 bg-white/50"
                }`}
              />
            ))}
          </div>
        )}
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8">
          <div className="mb-6 text-center">
            <h2 className="text-2xl font-bold text-slate-900">
              Accedi al tuo account
            </h2>
            <p className="text-sm text-slate-500 mt-2">
              Inserisci le tue credenziali per accedere al sistema
            </p>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 text-red-700 text-sm px-4 py-2 rounded-lg">
              {error}
            </div>
          )}

          <form className="space-y-5" onSubmit={handleSubmit}>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 outline-none focus:ring-2 focus:ring-sky-400 focus:bg-white transition"
                placeholder="Username"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Password
              </label>
              <input
                type="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 outline-none focus:ring-2 focus:ring-sky-400 focus:bg-white transition"
                placeholder="Password"
                required
              />
            </div>

            <button
              type="submit"
              className="w-full bg-sky-500 hover:bg-sky-600 text-white rounded-lg py-2.5 font-semibold transition shadow-sm"
            >
              Accedi
            </button>

            <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
              <div className="flex items-center gap-2 font-medium">
                <Lock size={16} />
                Secure login - TLS encrypted connection
              </div>
              <div className="mt-1 text-xs text-emerald-700">
                Accesso protetto: tutte le comunicazioni sono cifrate.
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              <div className="flex items-center gap-2 font-medium">
                <ShieldCheck size={16} />
                Messaggio GDPR friendly
              </div>
              <div className="mt-1 text-xs text-slate-600">
                I dati sono trattati in conformita al Regolamento UE 2016/679 (GDPR).
              </div>
            </div>
          </form>

          {registrationEnabled && (
            <div className="mt-6 border-t border-slate-100 pt-6 text-center">
              <p className="mb-3 text-sm text-slate-500">Non hai un account?</p>
              <Link to="/registrati" className="block w-full rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 font-semibold text-sky-700 transition hover:bg-sky-100">Registrati</Link>
              <p className="mt-3 text-xs text-slate-500">Crea il tuo account e scegli il piano per la tua attività.</p>
            </div>
          )}

          <div className="mt-5 flex items-center justify-center">
            <a
              href="mailto:assistenza@norma-haccp.com?subject=Supporto%20tecnico"
              className="inline-flex items-center gap-2 text-sm text-sky-600 hover:text-sky-700"
            >
              <LifeBuoy size={16} />
              Supporto tecnico
            </a>
          </div>

          <div className="mt-6 text-center text-xs text-slate-500 leading-6">
            <div className="flex flex-wrap justify-center gap-x-2">
              <Link to="/privacy" className="hover:text-slate-700">Privacy Policy</Link>
              <span>|</span>
              <Link to="/termini" className="hover:text-slate-700">Termini di Servizio</Link>
              <span>|</span>
              <Link to="/cookie-policy" className="hover:text-slate-700">Cookie Policy</Link>
              <span>|</span>
              <Link to="/security" className="hover:text-slate-700">Security</Link>
            </div>
            <div className="mt-3">
              © 2026 Norma HACCP - Food Safety Pilot - Norma Ai Evolution | MadOut S.r.l.
            </div>
            <div>
              P.I. IT15643551003 | Rea 1605146
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
